import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { apiClient, Site } from '../../services/api';

interface DeleteSiteModalProps {
    site: { id: string; name: string; totalDrivers: number } | null;
    onClose: () => void;
    onConfirm: (action: 'unassign' | 'reassign', targetSiteId?: string) => void;
    isDeleting: boolean;
}

const DeleteSiteModal: React.FC<DeleteSiteModalProps> = ({ site, onClose, onConfirm, isDeleting }) => {
    const [availableSites, setAvailableSites] = useState<Site[]>([]);
    const [loadingSites, setLoadingSites] = useState(false);
    const [choice, setChoice] = useState<'reassign' | 'unassign' | null>(null);
    const [targetSiteId, setTargetSiteId] = useState('');

    useEffect(() => {
        if (!site || site.totalDrivers === 0) return;
        setLoadingSites(true);
        apiClient.getSites({ active: true })
            .then(res => {
                setAvailableSites(res.data.filter(s => s.id !== site.id));
            })
            .catch(() => setAvailableSites([]))
            .finally(() => setLoadingSites(false));
    }, [site]);

    // Reset internal state when modal opens for a new site
    useEffect(() => {
        setChoice(null);
        setTargetSiteId('');
    }, [site?.id]);

    if (!site) return null;

    const hasDrivers = site.totalDrivers > 0;
    const hasOtherSites = availableSites.length > 0;

    const handleConfirm = () => {
        if (!hasDrivers) {
            onConfirm('unassign');
            return;
        }
        if (!hasOtherSites) {
            onConfirm('unassign');
            return;
        }
        if (choice === 'unassign') {
            onConfirm('unassign');
        } else if (choice === 'reassign' && targetSiteId) {
            onConfirm('reassign', targetSiteId);
        }
    };

    const isConfirmDisabled =
        isDeleting ||
        loadingSites ||
        (hasDrivers && hasOtherSites && (!choice || (choice === 'reassign' && !targetSiteId)));

    const renderTitle = () => {
        if (!hasDrivers) return 'Delete Site';
        if (!hasOtherSites) return 'Delete Site';
        return 'Delete Site';
    };

    const renderContent = () => {
        // Scenario 3: No drivers
        if (!hasDrivers) {
            return (
                <p className="text-gray-700">
                    <strong>{site.name}</strong> has no drivers assigned. Are you sure you want to delete it?
                </p>
            );
        }

        // Scenario 2: Drivers but no other sites
        if (!hasOtherSites) {
            if (loadingSites) {
                return (
                    <div className="flex items-center space-x-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Checking available sites...</span>
                    </div>
                );
            }
            return (
                <>
                    <p className="text-gray-700 mb-4">
                        <strong>{site.name}</strong> has <strong>{site.totalDrivers} driver{site.totalDrivers !== 1 ? 's' : ''}</strong> assigned.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <p className="text-sm text-yellow-800">
                            There are no other active sites to reassign drivers to. All {site.totalDrivers} driver{site.totalDrivers !== 1 ? 's' : ''} will become <strong>unassigned</strong> when this site is deleted.
                        </p>
                    </div>
                </>
            );
        }

        // Scenario 1: Drivers + other sites available
        if (loadingSites) {
            return (
                <div className="flex items-center space-x-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading available sites...</span>
                </div>
            );
        }

        return (
            <>
                <p className="text-gray-700 mb-4">
                    <strong>{site.name}</strong> has <strong>{site.totalDrivers} driver{site.totalDrivers !== 1 ? 's' : ''}</strong> assigned. Choose what to do with them before deleting:
                </p>

                {/* Option: Reassign */}
                <label className={`flex items-start space-x-3 p-3 border rounded-md cursor-pointer mb-3 transition-colors ${choice === 'reassign' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input
                        type="radio"
                        name="driverAction"
                        value="reassign"
                        checked={choice === 'reassign'}
                        onChange={() => setChoice('reassign')}
                        className="mt-0.5"
                        disabled={isDeleting}
                    />
                    <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">Move all drivers to another site</span>
                        {choice === 'reassign' && (
                            <select
                                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                                value={targetSiteId}
                                onChange={e => setTargetSiteId(e.target.value)}
                                disabled={isDeleting}
                            >
                                <option value="">Select a site...</option>
                                {availableSites.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name} ({s.code})
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </label>

                {/* Option: Unassign */}
                <label className={`flex items-start space-x-3 p-3 border rounded-md cursor-pointer transition-colors ${choice === 'unassign' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input
                        type="radio"
                        name="driverAction"
                        value="unassign"
                        checked={choice === 'unassign'}
                        onChange={() => setChoice('unassign')}
                        className="mt-0.5"
                        disabled={isDeleting}
                    />
                    <div>
                        <span className="text-sm font-medium text-gray-900">Leave all drivers unassigned</span>
                        <p className="text-xs text-gray-500 mt-0.5">Drivers will remain in the system with no site attached.</p>
                    </div>
                </label>
            </>
        );
    };

    const renderConfirmLabel = () => {
        if (isDeleting) return <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>;
        if (!hasDrivers) return 'Delete Site';
        if (!hasOtherSites) return 'Delete & Unassign Drivers';
        if (choice === 'reassign') return 'Delete & Reassign Drivers';
        if (choice === 'unassign') return 'Delete & Unassign Drivers';
        return 'Delete Site';
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{renderTitle()}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isDeleting}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {renderContent()}
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                        {renderConfirmLabel()}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteSiteModal;
